import { FC, PropsWithChildren, useState, useEffect, useRef } from "react"
import { useParam } from "@blitzjs/next"
import { useRouter } from "next/router"
import { Routes } from "@blitzjs/next"
import { PricingProvider, sonner } from "@superwall/ui"
import { trpc } from "app/core/trpcClient"
import { useApplicationContext } from "app/core/contexts/ApplicationContext"

const PROCESSED_STRIPE_KEY = "sw-stripe-priceids-callback"

/**
 * BillingContextProvider
 *
 * Wraps the PricingProvider from @superwall/ui and handles fetching
 * the subscription data from the API. This prevents duplicate subscription
 * fetching logic across different layouts.
 *
 * Usage: Wrap your layout content with this provider, similar to ApplicationContextProvider
 *
 * @param isOnboarding - Enable billing and redirect to dashboard after subscription (for onboarding flows)
 */
export const BillingContextProvider: FC<PropsWithChildren<{ isOnboarding?: boolean }>> = ({
  children,
  isOnboarding = false,
}) => {
  const router = useRouter()
  const { enabled } = useApplicationContext()
  const applicationIdParam = useParam("applicationId", "number")
  const applicationId =
    applicationIdParam ||
    (router.query.applicationId ? parseInt(router.query.applicationId as string) : null)
  const [loadingPriceIds, setLoadingPriceIds] = useState<string[] | null | undefined>(undefined)
  const hasProcessedSetup = useRef(false)

  const isBillingV2Enabled = isOnboarding || enabled("billing_v2")

  const {
    data: subscriptionData,
    isLoading: isLoadingSubscription,
    refetch,
  } = trpc.billing.subscription.get.useQuery(
    { applicationId: applicationId! },
    {
      enabled: !!applicationId,
      suspense: false,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    }
  )

  const subscription = subscriptionData ?? null

  const scheduledSubscription = subscription?.scheduledChange
    ? {
        stripePriceIds: subscription.scheduledChange.stripePriceIds,
        startDate: subscription.scheduledChange.effectiveDate,
      }
    : null

  const changeMutation = trpc.billing.subscription.change.useMutation()
  const openPortalMutation = trpc.billing.subscription.openPortal.useMutation()

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const priceIdsParam = urlParams.get("priceIds")

    if (priceIdsParam && applicationId && !hasProcessedSetup.current) {
      const processedPriceIds = sessionStorage.getItem(PROCESSED_STRIPE_KEY)
      if (processedPriceIds === priceIdsParam) {
        urlParams.delete("priceIds")
        const newUrl = `${window.location.pathname}${
          urlParams.toString() ? `?${urlParams.toString()}` : ""
        }`
        router.replace(newUrl, undefined, { shallow: true })
        return
      }

      hasProcessedSetup.current = true
      const priceIds = priceIdsParam.split(",")

      const completeSetup = async () => {
        try {
          sessionStorage.setItem(PROCESSED_STRIPE_KEY, priceIdsParam)

          setLoadingPriceIds(priceIds)

          await changeMutation.mutateAsync({
            applicationId,
            intent: "upgrade",
            targetPriceIds: priceIds,
          })

          urlParams.delete("priceIds")
          const newUrl = `${window.location.pathname}${
            urlParams.toString() ? `?${urlParams.toString()}` : ""
          }`
          router.replace(newUrl, undefined, { shallow: true })

          await refetch()

          sessionStorage.removeItem(PROCESSED_STRIPE_KEY)

          sonner.success({
            title: "Successfully subscribed!",
            description: "Your payment method has been saved and subscription is now active.",
          })

          if (isOnboarding && applicationId) {
            await router.push(Routes.ShowApplicationDashboardPage({ applicationId }))
          }
        } catch (err: any) {
          console.error(err)
          sessionStorage.removeItem(PROCESSED_STRIPE_KEY)
          sonner.error({
            title: "Failed to complete subscription",
            description: err.message,
          })

          urlParams.delete("priceIds")
          const newUrl = `${window.location.pathname}${
            urlParams.toString() ? `?${urlParams.toString()}` : ""
          }`
          router.replace(newUrl, undefined, { shallow: true })
        } finally {
          setLoadingPriceIds(undefined)
        }
      }

      completeSetup()
    }
  }, [applicationId, changeMutation, refetch, router, isOnboarding])

  const handlePlanChange = async (
    planId: string,
    priceIds: string[] | null,
    type?: "upgrade" | "downgrade"
  ) => {
    if (!applicationId) return

    if (planId === "enterprise") {
      window.open("https://superwall.com/contact/book-a-demo", "_blank")
      return
    }

    setLoadingPriceIds(priceIds ?? [])

    try {
      const currentUrl = window.location.href

      const intent = type === "downgrade" ? "downgrade" : "upgrade"

      const result = await changeMutation.mutateAsync({
        applicationId,
        intent,
        targetPriceIds: priceIds,
        successUrl: currentUrl,
        cancelUrl: currentUrl,
      })

      if (result.result === "success") {
        await refetch()
        sonner.success({
          title: result.message,
        })

        if (isOnboarding && applicationId) {
          await router.push(Routes.ShowApplicationDashboardPage({ applicationId }))
        }
        return
      }

      if (result.result === "scheduled") {
        await refetch()
        sonner.success({
          title: result.message,
          description: "Changes will take effect at the end of your current billing period.",
        })
        return
      }

      if (result.result === "contact_sales") {
        window.open(result.url, "_blank")
        return
      }

      if (result.result === "contact_support") {
        window.open(result.url, "_blank")
        return
      }

      if (result.result === "redirect" && result.url) {
        window.location.href = result.url
      }
    } catch (err: any) {
      console.error(err)
      sonner.error({
        title:
          type === "downgrade" ? "Failed to schedule downgrade" : "Failed to update subscription",
        description: err.message,
      })
    } finally {
      setLoadingPriceIds(undefined)
    }
  }

  const handleManage = async (isCustomPlan?: boolean) => {
    if (!applicationId) return

    const currentPriceIds = subscription?.stripePriceIds || []
    setLoadingPriceIds(currentPriceIds)

    try {
      if (subscription?.cancelAtPeriodEnd) {
        const result = await changeMutation.mutateAsync({
          applicationId,
          intent: "reactivate",
          targetPriceIds: null,
        })

        if (result.result === "success") {
          await refetch()
          sonner.success({
            title: result.message,
          })
          return
        }
      }

      const result = await openPortalMutation.mutateAsync({
        applicationId,
        returnUrl: window.location.href,
        restrictedPortal: isCustomPlan,
      })

      window.location.href = result.url
    } catch (err: any) {
      console.error(err)
      sonner.error({
        title: "Failed to manage subscription",
        description: err.message,
      })
    } finally {
      setLoadingPriceIds(undefined)
    }
  }

  return (
    <PricingProvider
      subscription={{
        data: subscription,
        isLoading: isLoadingSubscription,
      }}
      scheduledSubscription={scheduledSubscription}
      actions={{
        onPlanChange: handlePlanChange,
        onManage: handleManage,
      }}
      state={{
        loadingPriceIds: loadingPriceIds,
      }}
      disabled={!isBillingV2Enabled}
    >
      {children}
    </PricingProvider>
  )
}
